import React, { useState, useEffect, useCallback } from 'react';
import { 
  Clock, FileText, HelpCircle, CheckCircle, XCircle, 
  ChevronRight, AlertCircle, User, Calendar, Briefcase,
  RefreshCw, ExternalLink, Eye
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { formatDateShort } from '../lib/dateUtils';
import JoinedBadge from './leaves/JoinedBadge';

// Leave type labels
const LEAVE_TYPE_LABELS = {
  pl: 'Privilege Leave',
  sl: 'Sick Leave',
  cl: 'Casual Leave',
  wfh: 'Work From Home',
  comp: 'Comp Off',
  ml: 'Mandatory Holiday',
  ol: 'Optional Holiday'
};

// Action Card Component
function ActionCard({ 
  title, 
  icon: Icon, 
  count, 
  items, 
  loading, 
  color,
  onViewAll,
  renderItem 
}) {
  const colorClasses = {
    amber: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', badge: 'bg-amber-100 text-amber-800' },
    blue: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', badge: 'bg-blue-100 text-blue-800' },
    emerald: { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', badge: 'bg-emerald-100 text-emerald-800' },
    pink: { bg: 'bg-pink-50', border: 'border-pink-200', text: 'text-pink-700', badge: 'bg-pink-100 text-pink-800' }
  };

  const colors = colorClasses[color] || colorClasses.amber;

  if (loading) {
    return (
      <Card className={`${colors.border} border`}>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <Skeleton className="h-5 w-32" />
            <Skeleton className="h-6 w-6 rounded-full" />
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {[1, 2, 3].map(i => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={`${colors.border} border overflow-hidden`} data-testid={`action-card-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <CardHeader className={`${colors.bg} pb-3`}>
        <div className="flex items-center justify-between">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <Icon className={`w-5 h-5 ${colors.text}`} />
            {title}
          </CardTitle>
          {count > 0 && (
            <Badge className={`${colors.badge} border-0 font-bold`}>
              {count}
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="p-3 space-y-2 max-h-80 overflow-y-auto">
        {items.length === 0 ? (
          <div className="text-center py-6 text-neutral-500">
            <CheckCircle className="w-8 h-8 mx-auto mb-2 text-emerald-400" />
            <p className="text-sm">All caught up!</p>
          </div>
        ) : (
          <>
            {items.map((item, idx) => renderItem(item, idx))}
            {count > items.length && (
              <button
                onClick={onViewAll}
                className="w-full text-center py-2 text-sm text-neutral-500 hover:text-neutral-700 hover:bg-neutral-50 rounded-none transition-colors flex items-center justify-center gap-1"
              >
                View all {count} items
                <ChevronRight className="w-4 h-4" />
              </button>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

export default function AdminActionCenter({ api, onNavigate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // Leave approval modal state
  const [leaveModal, setLeaveModal] = useState({ open: false, leave: null, action: null });
  const [adminNote, setAdminNote] = useState('');
  const [processing, setProcessing] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const res = await api.get('/api/notices/admin/action-center');
      setData(res.data);
    } catch (error) {
      console.error('Failed to fetch action center data:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [api]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
  };

  const openLeaveModal = (leave, action) => {
    setLeaveModal({ open: true, leave, action });
    setAdminNote('');
  };

  const closeLeaveModal = () => {
    setLeaveModal({ open: false, leave: null, action: null });
    setAdminNote('');
  };

  const handleLeaveAction = async () => {
    if (!leaveModal.leave || !leaveModal.action) return;
    
    setProcessing(true);
    try {
      await api.put(`/api/admin/leaves/${leaveModal.leave._id}/approve?status=${leaveModal.action}&admin_note=${encodeURIComponent(adminNote)}`);
      toast.success(`Leave ${leaveModal.action === 'approved' ? 'approved' : 'rejected'} successfully`);
      closeLeaveModal();
      fetchData(); // Refresh data
    } catch (error) {
      toast.error(`Failed to ${leaveModal.action} leave: ${error.response?.data?.detail || error.message}`);
    } finally {
      setProcessing(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return formatDateShort(dateStr);
  };

  const getDaysAgo = (dateStr) => {
    if (!dateStr) return '';
    const created = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.floor((now - created) / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    return `${diffDays} days ago`;
  };

  // Calculate total pending items
  const totalPending = (data?.pending_leaves?.count || 0) + 
                       (data?.new_applications?.count || 0) + 
                       (data?.open_tickets?.count || 0);

  return (
    <div className="space-y-4" data-testid="admin-action-center">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-neutral-900 flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-amber-500" />
            Action Center
          </h2>
          <p className="text-sm text-neutral-500 mt-0.5">
            {totalPending > 0 ? `${totalPending} items need your attention` : 'All caught up!'}
          </p>
        </div>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={handleRefresh}
          disabled={refreshing}
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Action Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Pending Leave Approvals */}
        <ActionCard
          title="Leave Approvals"
          icon={Calendar}
          count={data?.pending_leaves?.count || 0}
          items={data?.pending_leaves?.items || []}
          loading={loading}
          color="amber"
          onViewAll={() => onNavigate?.('hr')}
          renderItem={(leave, idx) => (
            <div 
              key={idx} 
              className="p-3 rounded-none glass-card hover:border-amber-200 transition-colors"
            >
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2 min-w-0">
                  <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center text-amber-700 font-semibold text-sm shrink-0">
                    {leave.employee_name?.charAt(0) || '?'}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-neutral-900 truncate">{leave.employee_name}</p>
                    <p className="text-xs text-neutral-500">{leave.department}</p>
                  </div>
                </div>
                <Badge variant="outline" className="text-xs shrink-0">
                  {getDaysAgo(leave.created_at)}
                </Badge>
              </div>
              <div className="mb-3">
                <p className="text-sm text-neutral-700">
                  <span className="font-medium">{LEAVE_TYPE_LABELS[leave.leave_type] || leave.leave_type}</span>
                  {leave.is_half_day && <span className="text-amber-600"> (Half Day)</span>}
                </p>
                <p className="text-xs text-neutral-500 mt-0.5">
                  {formatDate(leave.start_date)}
                  {leave.end_date !== leave.start_date && ` - ${formatDate(leave.end_date)}`}
                  {' • '}{leave.days_count} day{leave.days_count !== 1 ? 's' : ''}
                  <JoinedBadge leave={leave} />
                </p>
              </div>
              <div className="flex gap-2">
                <Button 
                  size="sm" 
                  className="flex-1 h-8 bg-emerald-600 hover:bg-emerald-700"
                  onClick={() => openLeaveModal(leave, 'approved')}
                >
                  <CheckCircle className="w-3.5 h-3.5 mr-1" />
                  Approve
                </Button>
                <Button 
                  size="sm" 
                  variant="destructive" 
                  className="flex-1 h-8"
                  onClick={() => openLeaveModal(leave, 'rejected')}
                >
                  <XCircle className="w-3.5 h-3.5 mr-1" />
                  Reject
                </Button>
              </div>
            </div>
          )}
        />

        {/* New Applications */}
        <ActionCard
          title="Job Applications"
          icon={Briefcase}
          count={data?.new_applications?.count || 0}
          items={data?.new_applications?.items || []}
          loading={loading}
          color="blue"
          onViewAll={() => onNavigate?.('hr')}
          renderItem={(app, idx) => (
            <div 
              key={idx} 
              className="p-3 rounded-none glass-card hover:border-blue-200 transition-colors"
            >
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2 min-w-0">
                  <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-semibold text-sm shrink-0">
                    {app.full_name?.charAt(0) || '?'}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-neutral-900 truncate">{app.full_name}</p>
                    <p className="text-xs text-neutral-500 truncate">{app.email}</p>
                  </div>
                </div>
              </div>
              <p className="text-sm text-neutral-700 mb-2">
                Applied for: <span className="font-medium">{app.job_title || 'Open Position'}</span>
              </p>
              <div className="flex items-center justify-between">
                <span className="text-xs text-neutral-400">{getDaysAgo(app.created_at)}</span>
                <Button 
                  size="sm" 
                  variant="outline" 
                  className="h-7 text-xs"
                  onClick={() => onNavigate?.('hr')}
                >
                  <Eye className="w-3 h-3 mr-1" />
                  Review
                </Button>
              </div>
            </div>
          )}
        />

        {/* Open Tickets */}
        <ActionCard
          title="Help Tickets"
          icon={HelpCircle}
          count={data?.open_tickets?.count || 0}
          items={data?.open_tickets?.items || []}
          loading={loading}
          color="pink"
          onViewAll={() => onNavigate?.('helpdesk')}
          renderItem={(ticket, idx) => (
            <div 
              key={idx} 
              className="p-3 rounded-none glass-card hover:border-pink-200 transition-colors"
            >
              <div className="flex items-start justify-between mb-2">
                <p className="text-sm font-medium text-neutral-900 line-clamp-2">{ticket.subject}</p>
                <Badge 
                  variant="outline" 
                  className={`shrink-0 ml-2 text-xs ${
                    ticket.status === 'in_progress' ? 'border-amber-300 text-amber-700' : 'border-pink-300 text-pink-700'
                  }`}
                >
                  {ticket.status === 'in_progress' ? 'In Progress' : 'Open'}
                </Badge>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-xs text-neutral-500">
                  <User className="w-3 h-3" />
                  <span className="truncate max-w-24">{ticket.employee_name}</span>
                  <span>•</span>
                  <span>{getDaysAgo(ticket.created_at)}</span>
                </div>
                <Button 
                  size="sm" 
                  variant="outline" 
                  className="h-7 text-xs"
                  onClick={() => onNavigate?.('helpdesk')}
                >
                  <ExternalLink className="w-3 h-3 mr-1" />
                  Open
                </Button>
              </div>
            </div>
          )}
        />
      </div>

      {/* Leave Approval Modal */}
      <Dialog open={leaveModal.open} onOpenChange={(open) => !open && closeLeaveModal()}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {leaveModal.action === 'approved' ? (
                <>
                  <CheckCircle className="w-5 h-5 text-emerald-600" />
                  Approve Leave Request
                </>
              ) : (
                <>
                  <XCircle className="w-5 h-5 text-red-600" />
                  Reject Leave Request
                </>
              )}
            </DialogTitle>
            <DialogDescription>
              {leaveModal.leave && (
                <div className="mt-3 p-3 bg-neutral-50 rounded-none text-left">
                  <p className="font-medium text-neutral-900">{leaveModal.leave.employee_name}</p>
                  <p className="text-sm mt-1">
                    {LEAVE_TYPE_LABELS[leaveModal.leave.leave_type] || leaveModal.leave.leave_type}
                    {leaveModal.leave.is_half_day && ' (Half Day)'}
                  </p>
                  <p className="text-sm text-neutral-600">
                    {formatDate(leaveModal.leave.start_date)}
                    {leaveModal.leave.end_date !== leaveModal.leave.start_date && ` - ${formatDate(leaveModal.leave.end_date)}`}
                    {' • '}{leaveModal.leave.days_count} day{leaveModal.leave.days_count !== 1 ? 's' : ''}
                    <JoinedBadge leave={leaveModal.leave} />
                  </p>
                  {leaveModal.leave.reason && (
                    <p className="text-xs text-neutral-500 mt-2 italic">&quot;{leaveModal.leave.reason}&quot;</p>
                  )}
                </div>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <label className="text-sm font-medium text-neutral-700">
              Admin Note (Optional)
            </label>
            <Textarea
              value={adminNote}
              onChange={(e) => setAdminNote(e.target.value)}
              placeholder={leaveModal.action === 'approved' ? 'Any notes for the employee...' : 'Reason for rejection...'}
              className="mt-1"
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeLeaveModal} disabled={processing}>
              Cancel
            </Button>
            <Button 
              onClick={handleLeaveAction}
              disabled={processing}
              className={leaveModal.action === 'approved' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
              variant={leaveModal.action === 'rejected' ? 'destructive' : 'default'}
            >
              {processing ? (
                <RefreshCw className="w-4 h-4 animate-spin mr-2" />
              ) : null}
              {leaveModal.action === 'approved' ? 'Approve Leave' : 'Reject Leave'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
